import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  DataTable,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Toastify,
  Badge,
  SpinnerLoader,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Label,
  Input,
  Checkbox,
  PageHeader,
  Avatar,
  AvatarImage,
  AvatarFallback,
  MultiSelectCheckboxTag,
  DatePicker,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';
import { Textarea } from '@/components/ui/textarea';
import {
  RefreshCw,
  Plus,
  Edit,
  Search,
  Trash2,
  Eye,
  Smile,
} from 'lucide-react';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import moment from 'moment';

import './notification.styles.scss';
import { debounce } from '@lift-admin/utils/debounce';
import apiClient from '@lift-admin/utils/apiClient';
import { actionLog } from '../../App';
import { useAuthContext } from '@lift-admin/utils/provider';
import { getIsLoggingOut } from '@lift-admin/utils';

/** `readByFormatted` as MM/DD/YYYY. `createdAt` only for edit. Returns error message or null. */
function validateNotificationReadBy(
  readByFormatted: string,
  createdAt?: string | null
): string | null {
  const readBy = moment(readByFormatted, 'MM/DD/YYYY', true).startOf('day');
  if (!readBy.isValid()) {
    return 'Invalid read by date.';
  }
  const today = moment().startOf('day');
  if (readBy.isBefore(today)) {
    return 'Read by date cannot be in the past.';
  }
  if (createdAt) {
    const c = moment(createdAt).startOf('day');
    if (c.isValid() && readBy.isBefore(c)) {
      return 'Read by date cannot be before the created at date.';
    }
  }
  return null;
}

const isHtmlString = (value?: string) =>
  typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value);

const NotificationsComponent = () => {
  const { canEdit, canManage } = useAuthContext();
  const hasEditAccess = canEdit('notification_mgmt');
  const hasManageAccess = canManage('notification_mgmt');

  const [notifications, setNotificaionts] = useState<any>([]);
  const [notificationsBackup, setNotificaiontsBackup] = useState<any>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [showAddOrgDialog, setShowAddOrgDialog] = useState<any>(false);
  const [loadList, setLoadList] = useState<any>(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState<any>(false);
  const [allBrandsList, setAllBrandsList] = useState<any>([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [brandOptions, setBrandOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadingBrands, setLoadingBrands] = useState<boolean>(false);

  // seen-by dialog states
  const [showSeenByDialog, setShowSeenByDialog] = useState<boolean>(false);
  const [hideSeenByProgressDialog, setHideSeenByProgressDialog] = useState<boolean>(true);
  const [seenByList, setSeenByList] = useState<any[]>([]);

  // message view sheet (full message for larger content)
  const [messageSheetRow, setMessageSheetRow] = useState<any | null>(null);
  /** After closing the message sheet (e.g. overlay click), Radix can leave a ghost `click` on the cell under the cursor and reopen the sheet. Ignore opens from the table for a few ms. */
  const messageSheetReopenSuppressedUntilRef = useRef(0);

  const [isTeam, setIsTeam] = useState(true);
  
  const handleEmojiSelect = (event: any) => {
    setAddNotification({
      ...addNotification,
      message: addNotification.message + event.native,
    });
    setShowEmojiPicker(false);
  };
  
  const handleEmojiSelectEdit = (event: any) => {
    setEditNotifications({
      ...editNotifications,
      message: editNotifications.message + event.native,
    });
    setShowEmojiPicker(false);
  };

  const [addNotification, setAddNotification] = useState<any>({
    topic: '',
    [`sub_topic`]: '',
    message: '',
    read_by: moment().add(1, 'days').format('MM/DD/YYYY'),
    checked: false,
  });
  const [hideAddOrgProgressDialog, setHideAddOrgProgressDialog] = useState<any>(true);

  // Edit
  const [editNotifications, setEditNotifications] = useState<any>({
    topic: '',
    [`sub_topic`]: '',
    message: '',
    read_by: moment().add(1, 'days').format('MM/DD/YYYY'),
    id: '',
    created_at: '',
  });
  const [showEditNotificationDialog, setShowEditNotificationDialog] = useState<any>(false);
  const [hideEditOrgProgressDialog, setHideEditOrgProgressDialog] = useState<any>(true);

  // Delete
  const [aboutToDeleteNotification, setAboutToDeleteNotification] = useState<any>(null);
  const [hideDeleteOrgProgressDialog, setHideDeleteOrgProgressDialog] = useState<any>(true);

  const [searchText, setSearchText] = useState<any>('');

  // React version 18 2x rendering
  const get_orgs_ref = useRef(true);
  // Snapshot of the notification being edited, for before -> after change logs.
  const editOriginalRef = useRef<any>({});
  useEffect(() => {
    if (get_orgs_ref.current) {
      get_orgs_ref.current = false;
    }
    getNotifications();
  }, [page, rowsPerPage]);

  // Getting all notifications
  const getNotifications = async () => {
    try {
      setLoadList(true);
      const res1 = await apiClient.get(`/outreach/v1/notification/all`, {
        params: { page: page + 1, limit: rowsPerPage },
      });

      if (res1.status == 200) {
        const finalData = res1.data.data;
        setTotalItems(res1.data.meta.total);
        setTotalPages(res1.data.meta.totalPages);
        setLoadList(false);
        setNotificaionts(finalData);
        setNotificaiontsBackup(finalData);
      }
    } catch (error: any) {
      setLoadList(false);
      Toastify('error', error);
      console.error(error);
    }
  };

  // Add Notification to db
  const addNotifications = async () => {
    try {
      if (!addNotification.message || addNotification.message.length === 0) {
        Toastify('error', 'Message is required!');
        return;
      } else if (!addNotification.sub_topic || addNotification.sub_topic.length === 0) {
        Toastify('error', 'Sub-topic is required!');
        return;
      } else if (!addNotification.topic || addNotification.topic.length === 0) {
        Toastify('error', 'Topic is required!');
        return;
      } else if (!addNotification.read_by || addNotification.read_by.length === 0) {
        Toastify('error', 'Read by date is required!');
        return;
      }

      const readByErr = validateNotificationReadBy(addNotification.read_by, null);
      if (readByErr) {
        Toastify('error', readByErr);
        return;
      }

      setHideAddOrgProgressDialog(false);

      const res1 = await apiClient.post(`outreach/v1/notification/add`, {
        ...addNotification,
        is_team: isTeam,
        brand: isTeam ? [] : selectedBrandIds.map(id => parseInt(id)),
        read_by: moment(addNotification.read_by).format('DD/MM/YYYY'),
      });

      const desc1 = {
        message: `Notification "${addNotification.topic || 'Untitled'}" created`,
        status: res1.status,
        method: res1.config.method,
        url: res1.config.url,
        category: 'settings',
        details: [
          { label: 'Topic', value: addNotification.topic || 'Not Set' },
          { label: 'Sub Topic', value: (addNotification as any).sub_topic || 'Not Set' },
          { label: 'Message', value: addNotification.message || 'Not Set' },
          { label: 'Read By', value: addNotification.read_by ? moment(addNotification.read_by).format('DD/MM/YYYY') : 'Not Set' },
          { label: 'Audience', value: isTeam ? 'Team-wide' : `${selectedBrandIds.length} brand(s)` },
        ],
      };

      await actionLog?.actionLogTrack(JSON.stringify(desc1), false);

      setHideAddOrgProgressDialog(true);
      setShowAddOrgDialog(false);
      setAddNotification({
        topic: '',
        [`sub_topic`]: '',
        message: '',
        read_by: moment().add(1, 'days').format('MM/DD/YYYY'),
        checked: false,
      });
      setSelectedBrandIds([]);
      getNotifications();
      Toastify('success', 'Notification added successfully!');
    } catch (error) {
      setHideAddOrgProgressDialog(true);
      setShowAddOrgDialog(false);
      setAddNotification({
        topic: '',
        [`sub_topic`]: '',
        message: '',
      });
      setSelectedBrandIds([]);
      await actionLog?.actionLogTrack(JSON.stringify(error), true);
      Toastify('error', 'Failed to add notification');
    }
  };

  const handleAddNotification = async (e: any) => {
    setAddNotification({
      ...addNotification,
      [e.target.name]: e.target.value,
    });
  };

  // Edit organization
  const editOrganization = (notification: any) => {
    const rawReadBy = notification['read_by'];
    const parsedReadBy = rawReadBy && moment(rawReadBy).isValid()
      ? moment(rawReadBy).format('MM/DD/YYYY')
      : '';

    // Snapshot the original notification so we can log before -> after changes.
    editOriginalRef.current = {
      topic: notification.topic,
      sub_topic: notification.sub_topic,
      message: notification.message,
      read_by: parsedReadBy,
      is_team: notification.is_team,
      brand: Array.isArray(notification.brand) ? notification.brand.map((id: number) => String(id)) : [],
    };

    setEditNotifications({
      topic: notification.topic,
      [`sub_topic`]: notification[`sub_topic`],
      message: notification.message,
      read_by: parsedReadBy,
      id: notification.id,
      created_at: notification.created_at,
    });
    setIsTeam(notification.is_team);
    // Set selected brand IDs from notification
    if (notification.brand && Array.isArray(notification.brand)) {
      setSelectedBrandIds(notification.brand.map((id: number) => String(id)));
    } else {
      setSelectedBrandIds([]);
    }
    setShowEditNotificationDialog(true);
  };

  // Update notification
  const updateNotification = async () => {
    try {
      const readByErr = validateNotificationReadBy(
        editNotifications.read_by,
        editNotifications.created_at
      );
      if (readByErr) {
        Toastify('error', readByErr);
        return;
      }

      setHideEditOrgProgressDialog(false);

      const res2 = apiClient.put(
        `/outreach/v1/notification/update/${editNotifications.id}`,
        {
          ...editNotifications,
          is_team: isTeam,
          brand: isTeam ? [] : selectedBrandIds.map(id => parseInt(id)),
          read_by: moment(editNotifications.read_by).format('DD/MM/YYYY'),
        }
      );

      Promise.all([null, res2])
        .then(([resFinal1, resFinal2]: any[]) => {
          setHideEditOrgProgressDialog(true);
          setShowEditNotificationDialog(false);
          getNotifications();
          setSelectedBrandIds([]);

          const original = editOriginalRef.current || {};
          const newReadBy = editNotifications.read_by
            ? moment(editNotifications.read_by).format('DD/MM/YYYY')
            : '';
          const oldReadBy = original.read_by
            ? moment(original.read_by).format('DD/MM/YYYY')
            : '';
          const changes: Array<{ label: string; from?: string; to?: string; added?: string[]; removed?: string[] }> = [];
          const pushScalar = (label: string, from: any, to: any) => {
            if (String(from ?? '') !== String(to ?? '')) {
              changes.push({ label, from: String(from ?? 'Not Set'), to: String(to ?? 'Not Set') });
            }
          };
          pushScalar('Topic', original.topic, editNotifications.topic);
          pushScalar('Sub Topic', original.sub_topic, (editNotifications as any).sub_topic);
          pushScalar('Message', original.message, editNotifications.message);
          pushScalar('Read By', oldReadBy, newReadBy);
          pushScalar('Audience', original.is_team ? 'Team-wide' : 'Brand-specific', isTeam ? 'Team-wide' : 'Brand-specific');
          if (!isTeam) {
            const newBrandIds = selectedBrandIds.map(id => String(id));
            const oldBrandIds: string[] = Array.isArray(original.brand) ? original.brand : [];
            const added = newBrandIds.filter(id => !oldBrandIds.includes(id));
            const removed = oldBrandIds.filter(id => !newBrandIds.includes(id));
            if (added.length || removed.length) {
              changes.push({ label: 'Brands', added, removed });
            }
          }
          const changeSummary = changes
            .map(c => (c.added || c.removed ? c.label : `${c.label}: ${c.from} → ${c.to}`))
            .join('; ');
          const desc2 = {
            message: `Notification "${editNotifications.topic || 'Untitled'}" updated${changeSummary ? ` — ${changeSummary}` : ''}`,
            status: resFinal2.status,
            method: resFinal2.config.method,
            url: resFinal2.config.url,
            category: 'settings',
            changes,
          };
          actionLog?.actionLogTrack(JSON.stringify(desc2), false);
          Toastify('success', 'Notification updated successfully!');
        })
        .catch((error) => {
          actionLog?.actionLogTrack(JSON.stringify(error), true);
          setHideEditOrgProgressDialog(true);
          setShowEditNotificationDialog(false);
          setSelectedBrandIds([]);
          Toastify('error', 'Failed to update notification');
        });
    } catch (error: any) {
      Toastify('error', error.response?.data?.message || 'Failed to update notification');
      setHideEditOrgProgressDialog(true);
      setShowEditNotificationDialog(false);
      await actionLog?.actionLogTrack(JSON.stringify(error), true);
    }
  };

  const handleEditNotification = async (e: any) => {
    setEditNotifications({
      ...editNotifications,
      [e.target.name]: e.target.value,
    });
  };

  // Delete Notification
  const deleteNotification = async (noti: any) => {
    try {
      setHideDeleteOrgProgressDialog(false);

      const res2 = await apiClient.delete(`/outreach/v1/notification/delete/${noti.id}`);

      Promise.all([null, res2])
        .then(([resFinal1, resFinal2]: any[]) => {
          getNotifications();
          setHideDeleteOrgProgressDialog(true);
          setAboutToDeleteNotification(null);

          const desc2 = {
            message: `Notification "${noti.topic || 'Untitled'}" deleted`,
            status: resFinal2.status,
            method: resFinal2.config.method,
            url: resFinal2.config.url,
            category: 'settings',
            details: [
              { label: 'Topic', value: noti.topic || 'Not Set' },
              { label: 'Sub Topic', value: noti.sub_topic || 'Not Set' },
              { label: 'Message', value: noti.message || 'Not Set' },
            ],
          };
          actionLog?.actionLogTrack(JSON.stringify(desc2), false);
          Toastify('success', 'Notification deleted successfully!');
        })
        .catch((error) => {
          setAboutToDeleteNotification(null);
          setHideDeleteOrgProgressDialog(true);
          actionLog?.actionLogTrack(JSON.stringify(error), true);
          Toastify('error', error.response?.data?.message || 'Failed to delete notification');
        });
    } catch (error: any) {
      Toastify('error', error.response?.data?.message || 'Failed to delete notification');
      setHideDeleteOrgProgressDialog(true);
      setAboutToDeleteNotification(null);
    }
  };

  // Search result
  const searchResults = (query: any) => {
    let results = [];

    for (let i = 0; i < notificationsBackup.length; i++) {
      const notification = notificationsBackup[i];
      const queryLower = query.toLowerCase();

      const createdAtFormatted = moment(notification['created_at']).format('DD/MM/YYYY').toLowerCase();
      const readByFormatted = moment(notification['read_by']).format('DD/MM/YYYY').toLowerCase();

      if (
        notification['message']?.toLowerCase().includes(queryLower) ||
        notification['topic']?.toLowerCase().includes(queryLower) ||
        notification['sub_topic']?.toLowerCase().includes(queryLower) ||
        createdAtFormatted.includes(queryLower) ||
        readByFormatted.includes(queryLower)
      ) {
        results.push(notification);
      }
    }
    setNotificaionts(results);
  };

  const searchOptimized = useCallback(debounce(searchResults, 600), [notificationsBackup]);

  const listOfBrandsWithOrganizations = async () => {
    setLoadingBrands(true);
    try {
      const res = await apiClient.get(`/brand/v1/organizations/brandsAndOrganizations/all`);

      const filteredOrganizations = res.data.response
        .map((org: any) => ({
          ...org,
          brands: org.brands.filter((brand: any) => brand.is_active),
        }))
        .filter((org: any) => org.brands.length > 0);

      setAllBrandsList(filteredOrganizations);

      // Create brand options for MultiSelectTag
      const options: { label: string; value: string }[] = [];
      filteredOrganizations.forEach((org: any) => {
        org.brands.forEach((brand: any) => {
          options.push({
            label: `${brand.name} (${org.name})`,
            value: String(brand.id),
          });
        });
      });
      
      // Sort options alphabetically by label
      options.sort((a, b) => a.label.localeCompare(b.label));
      setBrandOptions(options);
    } catch (error: any) {
      console.error('Error retrieving brands with organizations:', error.response ? error.response.data : error.message);
      if (!getIsLoggingOut()) {
        Toastify('error', 'Failed to retrieve brands with organizations');
      }
    } finally {
      setLoadingBrands(false);
    }
  };

  // Fetch users who have seen the notification
  const viewSeenBy = async (noti: any) => {
    try {
      setHideSeenByProgressDialog(false);
      const res = await apiClient.get(`/outreach/v1/notification/seen-by/${noti.id}`);

      if (res.status === 200) {
        setSeenByList(res.data.response || []);
      } else {
        setSeenByList([]);
      }

      setShowSeenByDialog(true);
    } catch (error: any) {
      Toastify('error', error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setHideSeenByProgressDialog(true);
    }
  };


  // Table columns (lift-ui DataTable format)
  const dataTableColumns = useMemo(() => {
    const cols: TableColumn<any>[] = [
      {
        key: 'topic',
        header: 'Topic',
        accessor: 'topic',
        sortable: true,
        width: '120px',
        render: (_v: any, row: any) => <span className="font-medium truncate block max-w-[110px]" title={row.topic}>{row.topic}</span>,
      },
      {
        key: 'sub_topic',
        header: 'Sub Topic',
        accessor: 'sub_topic',
        width: '120px',
        render: (_v: any, row: any) => <span className="truncate block max-w-[110px]" title={row.sub_topic}>{row.sub_topic}</span>,
      },
      {
        key: 'message',
        header: 'Message',
        accessor: 'message',
        width: '280px',
        render: (_v: any, row: any) => (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => {
                    if (Date.now() < messageSheetReopenSuppressedUntilRef.current) return;
                    setMessageSheetRow(row);
                  }}
                  className="w-full text-left max-h-[120px] overflow-auto overflow-x-hidden text-sm min-w-0 block rounded p-1 -m-1 hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
                >
                  {isHtmlString(row.message)
                    ? <span className="block break-words" dangerouslySetInnerHTML={{ __html: row.message }} />
                    : <span className="block break-words">{row.message}</span>}
                </button>
              </TooltipTrigger>
              <TooltipContent>Click here to view message</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
      },
      {
        key: 'read_by',
        header: 'Read By',
        accessor: (row: any) =>
          row?.read_by && moment(row.read_by).isValid()
            ? moment(row.read_by).format('DD/MM/YYYY')
            : '-',
        width: '95px',
        align: 'center' as const,
      },
      {
        key: 'created_at',
        header: 'Created At',
        accessor: (row: any) => moment(row.created_at).format('DD/MM/YYYY'),
        sortable: true,
        width: '95px',
      },
      {
        key: 'status',
        header: 'Status',
        accessor: (row: any) =>
          row.is_team
            ? 'Teams'
            : (row?.brand?.length > 0 ? 'Brand' : row?.users_sub?.length > 0 ? 'User' : 'All'),
        width: '80px',
        render: (_v: any, row: any) => {
          const label = row.is_team
            ? 'Teams'
            : (row?.brand?.length > 0 ? 'Brand' : row?.users_sub?.length > 0 ? 'User' : 'All');
          return (
            <Badge variant={label === 'All' ? 'default' : 'secondary'}>
              {label}
            </Badge>
          );
        },
      },
      {
        key: 'seen_by',
        header: 'Seen By',
        accessor: (row: any) => row.id,
        width: '80px',
        align: 'center' as const,
        render: (_v: any, row: any) => (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => viewSeenBy(row)}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View Seen By</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
      },
    ];

    if (hasEditAccess || hasManageAccess) {
      cols.push({
        key: 'actions',
        header: 'Actions',
        accessor: (row: any) => row.id,
        width: '100px',
        align: 'center' as const,
        render: (_v: any, row: any) => {
          const isWorkflowNotification = (row?.sub_topic ?? '').toString().trimStart().startsWith('Workflow:');
          const hasHtmlTable = typeof row?.message === 'string' && /<table[\s>]/i.test(row.message);
          const isActionsDisabled = isWorkflowNotification || hasHtmlTable;
          const disabledTooltip = isActionsDisabled
            ? (isWorkflowNotification && hasHtmlTable
                ? 'Edit is disabled for Workflow notifications and messages with HTML tables'
                : isWorkflowNotification
                  ? 'Edit is disabled for Workflow notifications'
                  : 'Edit is disabled for messages containing HTML tables')
            : 'Edit';
          const deleteDisabledTooltip = isActionsDisabled
            ? (isWorkflowNotification && hasHtmlTable
                ? 'Delete is disabled for Workflow notifications and messages with HTML tables'
                : isWorkflowNotification
                  ? 'Delete is disabled for Workflow notifications'
                  : 'Delete is disabled for messages containing HTML tables')
            : 'Delete';
          return (
            <div className="flex items-center gap-2 justify-center">
              {hasEditAccess && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={isActionsDisabled ? 'inline-flex cursor-not-allowed opacity-50' : ''}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => {
                            if (!isActionsDisabled) {
                              editOrganization(row);
                              listOfBrandsWithOrganizations();
                            }
                          }}
                          disabled={isActionsDisabled}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{disabledTooltip}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {hasManageAccess && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={isActionsDisabled ? 'inline-flex cursor-not-allowed opacity-50' : ''}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                          onClick={() => {
                            if (!isActionsDisabled) setAboutToDeleteNotification(row);
                          }}
                          disabled={isActionsDisabled}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{deleteDisabledTooltip}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        },
      });
    }

    return cols;
  }, [hasEditAccess, hasManageAccess]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      <PageHeader
        title={`Notifications ${!searchText.trim() ? `(${notifications.length})` : `(${notifications.length} of ${notificationsBackup.length})`}`}
        renderCustomTop={
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => getNotifications()}
              className="flex items-center gap-2 h-8 px-3"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            {hasEditAccess && (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  setShowAddOrgDialog(true);
                  listOfBrandsWithOrganizations();
                }}
                className="bg-primary hover:bg-primary/90 flex items-center gap-2 h-8 px-3"
              >
                <Plus className="h-4 w-4" />
                Add Notification
              </Button>
            )}
            <div className="relative ml-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search notifications..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  searchOptimized(e.target.value);
                }}
                className="pl-10 w-80"
              />
            </div>
          </div>
        }
      />

      <div className="flex-1 flex flex-col min-h-0 px-8 pt-8 pb-4">
        <div className="flex-1 min-h-0">
          <DataTable
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="calc(100vh - 170px)"
            columns={dataTableColumns}
            data={notifications}
            loading={loadList}
            emptyMessage="No notifications found."
            stickyHeader
            stickyFooter
            showActions={false}
            pagination={{
              currentPage: page + 1,
              totalPages: Math.max(1, totalPages),
              pageSize: rowsPerPage,
              totalItems,
              onPageChange: (p) => setPage(p - 1),
              onPageSizeChange: (size) => { setRowsPerPage(size); setPage(0); },
              pageSizeOptions: [25, 50, 100, 200],
            }}
          />
        </div>
      </div>

      {/* Add Notification Sheet */}
      <Sheet open={showAddOrgDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddOrgDialog(false);
          setAddNotification({
            topic: '',
            sub_topic: '',
            message: '',
            read_by: moment().add(1, 'days').format('MM/DD/YYYY'),
            checked: false,
          });
          setSelectedBrandIds([]);
          setShowEmojiPicker(false);
        }
      }}>
        <SheetContent
          side="right"
          className="w-[600px] sm:max-w-[600px] flex flex-col h-full"
        >
          <SheetHeader>
            <SheetTitle>Add Notification</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="space-y-4 p-4">
              {/* Topic */}
              <div className="space-y-2">
                <Label htmlFor="topic">Topic *</Label>
                <Input
                  id="topic"
                  name="topic"
                  placeholder="Enter topic"
                  value={addNotification.topic}
                  onChange={handleAddNotification}
                />
              </div>

              {/* Sub Topic and Date */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sub_topic">Sub Topic *</Label>
                  <Input
                    id="sub_topic"
                  name="sub_topic"
                    placeholder="Enter sub topic"
                    value={addNotification.sub_topic}
                  onChange={handleAddNotification}
                />
                </div>
                <div className="space-y-2">
                  <Label>Read By Date *</Label>
                  <DatePicker
                    placeholder="Select date"
                    value={addNotification.read_by ? moment(addNotification.read_by, 'MM/DD/YYYY').toDate() : undefined}
                    onChange={(date: Date) => {
                      const formatted = moment(date).format('MM/DD/YYYY');
                      const err = validateNotificationReadBy(formatted, null);
                      if (err) {
                        Toastify('error', err);
                        return;
                      }
                      setAddNotification({
                        ...addNotification,
                        read_by: formatted,
                      });
                    }}
                    disabled={false}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <div className="relative">
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Enter message"
                    value={addNotification.message}
                    onChange={handleAddNotification}
                    className="min-h-[120px] pr-10 resize-y"
                    rows={4}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-2 h-8 w-8 p-0"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    <Smile className="h-4 w-4" />
                  </Button>
                </div>
                {showEmojiPicker && (
                  <div className="absolute z-50 mt-2">
                    <Picker
                      data={data}
                      previewPosition="top"
                      theme="light"
                      onEmojiSelect={handleEmojiSelect}
                    />
                  </div>
                )}
              </div>

              {/* Is Team Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isTeam"
                  checked={isTeam}
                  onCheckedChange={(checked) => setIsTeam(checked as boolean)}
                />
                <Label htmlFor="isTeam" className="font-normal cursor-pointer">
                  Is Team Notification Only
                </Label>
              </div>

              {/* Brand Section - Only show when not team-only */}
              {!isTeam && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Select Brands</Label>
                  {loadingBrands && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <SpinnerLoader size={16} />
                        <span>Loading...</span>
                    </div>
                  )}
                </div>
                  <MultiSelectCheckboxTag
                    placeholder="Select brands..."
                    value={selectedBrandIds}
                    onChange={setSelectedBrandIds}
                    options={brandOptions}
                    containerClassName="w-full"
                    showSelectAll
                  />
                </div>
              )}
              </div>
              </div>
          <SheetFooter className="border-t pt-4 mt-4">
          <Button
              variant="outline"
            onClick={() => {
              setShowAddOrgDialog(false);
              setAddNotification({
                topic: '',
                  sub_topic: '',
                message: '',
                read_by: moment().add(1, 'days').format('MM/DD/YYYY'),
                checked: false,
              });
                setSelectedBrandIds([]);
              }}
            >
            Cancel
          </Button>
            {hasEditAccess && (
              <Button
                onClick={addNotifications}
                disabled={!hideAddOrgProgressDialog}
              >
                {hideAddOrgProgressDialog ? 'Add' : <SpinnerLoader size={16} />}
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit Notification Sheet */}
      <Sheet open={showEditNotificationDialog} onOpenChange={(open) => {
        if (!open) {
          setShowEditNotificationDialog(false);
          setEditNotifications({
            topic: '',
            sub_topic: '',
            message: '',
            read_by: moment().add(1, 'days').format('MM/DD/YYYY'),
            id: '',
            created_at: '',
          });
          setSelectedBrandIds([]);
          setShowEmojiPicker(false);
        }
      }}>
        <SheetContent
          side="right"
          className="w-[600px] sm:max-w-[600px] flex flex-col h-full"
        >
          <SheetHeader>
            <SheetTitle className="text-xl font-semibold">Edit Notification</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="space-y-4 p-4">
              {/* Topic */}
              <div className="space-y-2">
                <Label htmlFor="edit-topic">Topic *</Label>
                <Input
                  id="edit-topic"
                  name="topic"
                  placeholder="Enter topic"
                  value={editNotifications.topic || ''}
                  onChange={handleEditNotification}
                />
              </div>

              {/* Sub Topic and Date */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-sub_topic">Sub Topic *</Label>
                  <Input
                    id="edit-sub_topic"
                    name="sub_topic"
                    placeholder="Enter sub topic"
                    value={editNotifications.sub_topic || ''}
                    onChange={handleEditNotification}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Read By Date *</Label>
                  <DatePicker
                    placeholder="Select date"
                    value={(() => {
                      if (!editNotifications.read_by) return undefined;
                      const d = moment(editNotifications.read_by, 'MM/DD/YYYY');
                      return d.isValid() ? d.toDate() : undefined;
                    })()}
                    onChange={(date: Date) => {
                      const formatted = moment(date).format('MM/DD/YYYY');
                      const err = validateNotificationReadBy(
                        formatted,
                        editNotifications.created_at
                      );
                      if (err) {
                        Toastify('error', err);
                        return;
                      }
                      setEditNotifications({
                        ...editNotifications,
                        read_by: formatted,
                      });
                    }}
                    disabled={false}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="edit-message">Message *</Label>
                <div className="relative">
                  <Textarea
                    id="edit-message"
                    name="message"
                    placeholder="Enter message"
                    value={editNotifications.message || ''}
                    onChange={handleEditNotification}
                    className="min-h-[120px] pr-10 resize-y"
                    rows={4}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-2 h-8 w-8 p-0"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    <Smile className="h-4 w-4" />
                  </Button>
                </div>
                {showEmojiPicker && (
                  <div className="absolute z-50 mt-2">
                    <Picker
                      data={data}
                      previewPosition="top"
                      theme="light"
                      onEmojiSelect={handleEmojiSelectEdit}
                    />
                  </div>
                )}
              </div>

              {/* Is Team Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-isTeam"
                  checked={isTeam}
                  onCheckedChange={(checked) => setIsTeam(checked as boolean)}
                />
                <Label htmlFor="edit-isTeam" className="font-normal cursor-pointer">
                  Is Team Notification Only
                </Label>
              </div>

              {/* Brand Section - Only show when not team-only */}
              {!isTeam && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Select Brands</Label>
                    {loadingBrands && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
                          <SpinnerLoader size={16} className="h-4 w-4" />
                        </span>
                        <span>Loading...</span>
                      </div>
                    )}
                  </div>
                  <MultiSelectCheckboxTag
                    placeholder="Select brands..."
                    value={selectedBrandIds}
                    onChange={setSelectedBrandIds}
                    options={brandOptions}
                    containerClassName="w-full"
                    showSelectAll
                  />
                </div>
              )}
            </div>
          </div>
          <SheetFooter className="px-6 py-4 border-t flex justify-end gap-2">
          <Button
              variant="outline"
            onClick={() => {
              setShowEditNotificationDialog(false);
              setEditNotifications({
                topic: '',
                  sub_topic: '',
                message: '',
                read_by: moment().add(1, 'days').format('MM/DD/YYYY'),
                  id: '',
                created_at: '',
              });
                setSelectedBrandIds([]);
              }}
            >
            Cancel
          </Button>
            {hasEditAccess && (
              <Button
                onClick={updateNotification}
                disabled={!hideEditOrgProgressDialog}
              >
                {hideEditOrgProgressDialog ? (
                  'Update'
                ) : (
                  <span className="inline-flex h-4 w-4 items-center justify-center">
                    <SpinnerLoader size={16} className="h-4 w-4" />
                  </span>
                )}
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Notification Dialog */}
      <Dialog open={aboutToDeleteNotification !== null} onOpenChange={(open) => {
        if (!open) setAboutToDeleteNotification(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Notification</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this notification? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAboutToDeleteNotification(null)}>
            Cancel
          </Button>
            {hasManageAccess && (
              <Button
                variant="destructive"
                onClick={() => {
                  if (hideDeleteOrgProgressDialog) {
                    deleteNotification(aboutToDeleteNotification);
                  }
                }}
                disabled={!hideDeleteOrgProgressDialog}
              >
                {hideDeleteOrgProgressDialog ? 'Delete' : <SpinnerLoader size={16} />}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message View Sheet (full message for larger content) */}
      <Sheet open={messageSheetRow !== null} onOpenChange={(open) => {
        if (!open) {
          messageSheetReopenSuppressedUntilRef.current = Date.now() + 200;
          setMessageSheetRow(null);
        }
      }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-3xl flex flex-col"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle>
              {messageSheetRow ? `${messageSheetRow.topic}${messageSheetRow.sub_topic ? ` – ${messageSheetRow.sub_topic}` : ''}` : 'Message'}
            </SheetTitle>
            {messageSheetRow && (
              <p className="text-sm text-muted-foreground mt-1">
                Read by {messageSheetRow?.read_by && moment(messageSheetRow.read_by).isValid() ? moment(messageSheetRow.read_by).format('DD/MM/YYYY') : '-'}
                {messageSheetRow.created_at && ` · Created ${moment(messageSheetRow.created_at).format('DD/MM/YYYY')}`}
              </p>
            )}
          </SheetHeader>
          <div className="flex-1 overflow-y-auto py-4 min-h-0">
            {messageSheetRow?.message != null ? (
              <div className="prose prose-sm max-w-none break-words">
                {isHtmlString(messageSheetRow.message) ? (
                  <div
                    className="notification-message-content"
                    dangerouslySetInnerHTML={{ __html: messageSheetRow.message }}
                  />
                ) : (
                  <p className="whitespace-pre-wrap">{messageSheetRow.message}</p>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">No message.</p>
            )}
          </div>
          <SheetFooter className="border-t pt-4 flex-shrink-0">
            <Button variant="outline" onClick={() => setMessageSheetRow(null)}>
              Close
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Seen By Sheet */}
      <Sheet open={showSeenByDialog} onOpenChange={(open) => {
        if (!open) {
          setShowSeenByDialog(false);
          setSeenByList([]);
        }
      }}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
          <SheetHeader>
            <SheetTitle>Seen By Users</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto py-4">
            {!hideSeenByProgressDialog ? (
              <div className="flex justify-center py-8">
                <SpinnerLoader size={32} />
              </div>
            ) : seenByList.length > 0 ? (
              <div className="space-y-3">
                {seenByList.map((user: any, idx: number) => (
                  <div
                    key={user.id || idx}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user.image} alt={user.name} />
                      <AvatarFallback>
                        {user.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">
                No users have seen this notification yet.
              </p>
            )}
          </div>
          <SheetFooter className="border-t pt-4 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowSeenByDialog(false);
                setSeenByList([]);
              }}
            >
              Close
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default NotificationsComponent;
